import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { gql, useApolloClient, useReactiveVar } from "@apollo/client";
import TitleBar, { WithfriendsAction } from "./WithfriendsTitleBar";
import Button, { ButtonVariant } from "./Button";
import ErrorScreen from "./ErrorScreen";
import LoadingScreen from "./LoadingScreen";
import ChooseYourFighter from "./ChooseYourFighter";
import { currentBusinessIdVar, primaryActionLoadingVar, startPrimaryAction, completePrimaryAction, membershipsHaveBeenEditedVar, dummyFunc, currentShopifyVersionVar, createAccountJobIdVar, pageHasUnsavedChangesVar, clearUnsavedTierEdits } from "../helpers/client";
import { useBusinessMembershipTiersQuery, useMembershipsCustomerAnalyticsReportQuery, usePollCreateAccountJobQuery, usePollImportCompletedQuery } from "../types";
import { Redirect } from "@shopify/app-bridge/actions";
import { useAppBridge } from "@shopify/app-bridge-react";
import { saveMemberships } from "../cache/transformers";
// @ts-ignore
import { useNavigate } from "react-router-dom";
import NotConnectedScreen from "./NotConnectedScreen";
import { i18nManager } from "../helpers/tools";
import { I18nContext } from "@shopify/react-i18n";
gql `
  query MembershipsCustomerAnalyticsReport($id: ID!) {
    business(id: $id) {
      id
      importAnalytics {
        averageOrderValue {
          amount
        }
        customerVisitFrequency
      }
    }
  }

  query BusinessMembershipTiers($id: ID!) {
    business(id: $id) {
      id
      dashboardUrl
      status
      membershipTiers(kind:Live) {
        defaultMode
        id
        name
      }
    }
  }
`;
const MembershipTiers = ({ useSyncShopifyDataMutation = dummyFunc, VisitShopifyProduct, standalone = false }) => {
    var _a, _b;
    const client = useApolloClient();
    const currentBusinessId = useReactiveVar(currentBusinessIdVar);
    const primaryActionLoading = useReactiveVar(primaryActionLoadingVar);
    const membershipsHaveBeenEdited = useReactiveVar(membershipsHaveBeenEditedVar);
    const currentShopifyVersion = useReactiveVar(currentShopifyVersionVar);
    // TODO - can't seem to abstract this out without React crashing, so we gotta copy and paste it into any relevant file
    let app, redirect = null;
    let navigate = useNavigate();
    try {
        app = useAppBridge();
        redirect = Redirect.create(app);
    }
    catch (e) { }
    const VisitLink = (url) => {
        (redirect ?
            redirect.dispatch(Redirect.Action.APP, url) :
            navigate(url));
    };
    // TODO - end
    const { data, loading, error } = useBusinessMembershipTiersQuery({
        variables: {
            id: currentBusinessId,
        },
    });
    const { data: analyticsReport, stopPolling: stopPollingAnalytics } = useMembershipsCustomerAnalyticsReportQuery({
        variables: {
            id: currentBusinessId,
        },
        skip: primaryActionLoading,
        pollInterval: 2000,
    });
    const createAccountJobId = useReactiveVar(createAccountJobIdVar);
    const { data: importDataV1, stopPolling: stopPollingImportDataV1 } = usePollCreateAccountJobQuery({
        variables: {
            id: createAccountJobId,
        },
        skip: !createAccountJobId || primaryActionLoading,
        pollInterval: 2000
    });
    const { data: importDataV2, stopPolling: stopPollingImportDataV2 } = usePollImportCompletedQuery((currentShopifyVersion == 2) ? { variables: { id: currentBusinessId }, skip: !currentBusinessId || primaryActionLoading, pollInterval: 5000 } : null);
    const [syncToShopify] = useSyncShopifyDataMutation({
        variables: {
            id: currentBusinessId
        },
        skip: !currentBusinessId,
    });
    if (!currentBusinessId)
        return _jsx(NotConnectedScreen, { action: WithfriendsAction.Memberships });
    if (loading)
        return _jsx(LoadingScreen, {});
    if (error)
        return _jsx(ErrorScreen, { message: error.message });
    if (!data)
        return null;
    if (!createAccountJobId || (importDataV1 === null || importDataV1 === void 0 ? void 0 : importDataV1.jobFinished)) {
        stopPollingImportDataV1();
        if (currentShopifyVersion != 2)
            stopPollingAnalytics();
    }
    if (!!((_a = importDataV2 === null || importDataV2 === void 0 ? void 0 : importDataV2.business) === null || _a === void 0 ? void 0 : _a.importAnalytics) || currentShopifyVersion != 2) {
        stopPollingImportDataV2();
        if (currentShopifyVersion == 2)
            stopPollingAnalytics();
    }
    const saveMembershipsButton = async () => {
        startPrimaryAction();
        await saveMemberships(currentBusinessId, client, { makeNonDefault: membershipsHaveBeenEdited });
        await syncToShopify();
        pageHasUnsavedChangesVar(false);
        clearUnsavedTierEdits();
        completePrimaryAction();
    };
    const continueButton = async () => {
        await saveMembershipsButton();
        VisitLink("/profile");
    };
    const customerAnalyticsReport = (_b = analyticsReport === null || analyticsReport === void 0 ? void 0 : analyticsReport.business) === null || _b === void 0 ? void 0 : _b.importAnalytics;
    const averageOrderValue = (customerAnalyticsReport === null || customerAnalyticsReport === void 0 ? void 0 : customerAnalyticsReport.averageOrderValue.amount) || 1000;
    const customerVisitFrequency = (customerAnalyticsReport === null || customerAnalyticsReport === void 0 ? void 0 : customerAnalyticsReport.customerVisitFrequency) || 1.0;
    let extraButtons = (id) => {
        return _jsx(_Fragment, {});
    };
    if (VisitShopifyProduct) {
        extraButtons = (id) => {
            return _jsx(VisitShopifyProduct, { tierId: id });
        };
    }
    return (_jsxs("div", { className: "flex flex-col justify-center", children: [_jsx(TitleBar, { primaryAction: WithfriendsAction.SaveMemberships, action: WithfriendsAction.Memberships, useSyncShopifyDataMutation: useSyncShopifyDataMutation }), _jsx("div", { className: "w-full flex flex-wrap justify-center", children: _jsx(I18nContext.Provider, { value: i18nManager(), children: _jsx(ChooseYourFighter, { standalone: standalone, aov: averageOrderValue, cvf: customerVisitFrequency, businessId: currentBusinessId, extraButtons: extraButtons, useSyncShopifyDataMutation: useSyncShopifyDataMutation }) }) }), _jsx("div", { className: "w-full my-4" }), primaryActionLoading ? (_jsx("div", { className: "w-full flex flex-wrap justify-center", children: _jsx("div", { className: "flex max-w-xl w-full mb-16 sm:mb-0", children: _jsx(Button, { variant: ButtonVariant.Salmon, disabled: true, children: "Saving..." }) }) })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-full flex flex-wrap justify-center mb-4", children: _jsx("div", { className: "flex max-w-xl w-full", children: _jsx(Button, { variant: standalone ? ButtonVariant.Salmon : ButtonVariant.Outlined, onClick: saveMembershipsButton, children: "Save Memberships" }) }) }), !standalone && (_jsx("div", { className: "w-full flex flex-wrap justify-center", children: _jsx("div", { className: "flex max-w-xl w-full mb-16 sm:mb-0", children: _jsx(Button, { variant: ButtonVariant.Salmon, onClick: continueButton, children: "Save and Continue to Brand" }) }) }))] })), _jsx("div", { className: "w-full flex-auto" })] }));
};
export default MembershipTiers;
